// Festival-derived accent set. A mandal is a celebration circle, so the palette
// comes from Navratri colour rather than from a corporate dashboard.
//
// One hue per domain, used everywhere that domain appears - nav icon, stat card,
// chart series, chip. Colour is a wayfinding device here, not decoration: if a
// number is teal it is about meetings, if it is vermilion it is money owed.
// Festival accents, in two weights.
//
// A single set cannot serve both grounds: the hues that read well on warm paper
// are too dark on the charcoal surface, and vice versa. Both sets keep the same
// meaning per hue - only the weight changes with the mode.
const SETS = {
  light: {
    gold: '#B5822A',
    marigold: '#C77A15',
    emerald: '#1F8B5B',
    vermilion: '#C03A18',
    magenta: '#B02659',
    indigo: '#5344C4',
    teal: '#17847A',
    silver: '#6B7784',
  },
  dark: {
    gold: '#E0A32E',
    marigold: '#F2A33C',
    emerald: '#3FB985',
    vermilion: '#E8552F',
    magenta: '#D8477E',
    indigo: '#8E80F5',
    teal: '#2FB5A8',
    silver: '#B9C2CC',
  },
};

// The active mode, set by ThemeModeProvider. Kept in a module variable so the
// exports below stay plain imports - no component needs to thread the mode
// through to read an accent.
let activeMode = 'light';
export const setAccentMode = (mode) => { activeMode = mode === 'dark' ? 'dark' : 'light'; };

// Reads resolve against whichever mode is active at render time
export const ACCENTS = new Proxy({}, {
  get: (_target, key) => SETS[activeMode][key],
  ownKeys: () => Reflect.ownKeys(SETS.light),
  getOwnPropertyDescriptor: () => ({ enumerable: true, configurable: true }),
});

// What each area of the app is coloured by
export const DOMAIN = {
  dashboard: ACCENTS.gold,
  payments: ACCENTS.emerald,
  penalties: ACCENTS.vermilion,
  expenses: ACCENTS.marigold,
  members: ACCENTS.indigo,
  meetings: ACCENTS.teal,
  distribution: ACCENTS.magenta,
  loans: ACCENTS.magenta,
  rules: ACCENTS.silver,
  admin: ACCENTS.gold,
  audit: ACCENTS.silver,
};

// Categorical series for charts, ordered for maximum separation between
// neighbours. Also mode-aware, hence the proxy rather than a fixed array.
const seriesOrder = ['emerald', 'vermilion', 'indigo', 'marigold', 'teal', 'magenta'];
export const SERIES = new Proxy([], {
  get: (_target, key) => {
    if (key === 'length') return seriesOrder.length;
    if (typeof key === 'string' && /^\d+$/.test(key)) return SETS[activeMode][seriesOrder[Number(key)]];
    if (key === Symbol.iterator) return function* () { for (const k of seriesOrder) yield SETS[activeMode][k]; };
    return Array.prototype[key];
  },
});

// A soft wash of a hue over the dark ground - readable, still clearly coloured
export const tint = (hex, alpha = 0.14) => {
  const value = hex.replace('#', '');
  const r = parseInt(value.slice(0, 2), 16);
  const g = parseInt(value.slice(2, 4), 16);
  const b = parseInt(value.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// Stable colour per person so a member keeps the same identity colour everywhere
export const colorForName = (name = '') => {
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) {
    hash = (hash * 31 + name.charCodeAt(i)) % 997;
  }
  const wheel = ['indigo', 'teal', 'magenta', 'marigold', 'emerald', 'gold'];
  return SETS[activeMode][wheel[hash % wheel.length]];
};
